(() => {
  if (window.__niniProductCardThumbsReady) return;
  window.__niniProductCardThumbsReady = true;

  const cardSelector = '.nini-product-card, .nini-mobile-product-card, [data-nini-product-card]';
  const imageSelector = '.nini-product-card__image, .nini-mobile-product-card__image';
  const thumbSelector = '[data-nini-variant-thumb], .nini-variant-thumbs__item';
  const productJsonCache = new Map();

  const normalizeImageUrl = (value) => {
    if (!value) return '';
    return value.startsWith('//') ? `https:${value}` : value;
  };

  const imageUrlWithWidth = (value, width) => {
    const url = normalizeImageUrl(value);
    if (!url) return '';
    if (url.includes('width=')) return url.replace(/([?&])width=\d+/g, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const imageKey = (value) => normalizeImageUrl(value).replace(/([?&])width=\d+/g, '').replace(/[?&]$/, '');

  const getCards = (scope = document) => {
    const cards = [];
    if (scope.matches?.(cardSelector)) cards.push(scope);
    scope.querySelectorAll?.(cardSelector).forEach((card) => cards.push(card));
    return cards;
  };

  const getThumbs = (card) => (
    Array.from(card.querySelectorAll(thumbSelector))
      .filter((thumb) => thumb.hasAttribute('data-nini-variant-thumb') || thumb.querySelector('img'))
  );

  const getThumbImage = (thumb) => thumb.querySelector('img');

  const getThumbSrc = (thumb) => (
    normalizeImageUrl(thumb.getAttribute('data-nini-main-src'))
    || imageUrlWithWidth(getThumbImage(thumb)?.currentSrc || getThumbImage(thumb)?.src, 720)
  );

  const getThumbSrcset = (thumb) => {
    const srcset = thumb.getAttribute('data-nini-main-srcset');
    if (srcset) return srcset.replace(/(^|,\s*)\/\//g, '$1https://');

    const src = getThumbImage(thumb)?.currentSrc || getThumbImage(thumb)?.src;
    if (!src) return '';

    return [360, 540, 720, 900].map((width) => `${imageUrlWithWidth(src, width)} ${width}w`).join(', ');
  };

  const setActiveThumb = (thumb, card) => {
    getThumbs(card).forEach((item) => {
      const isCurrent = item === thumb;
      item.classList.toggle('is-active', isCurrent);
      item.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
  };

  const swapImage = (thumb) => {
    const card = thumb.closest(cardSelector);
    const image = card?.querySelector(imageSelector);
    const nextSrc = getThumbSrc(thumb);
    const nextSrcset = getThumbSrcset(thumb);
    const nextAlt = thumb.getAttribute('data-nini-main-alt') || getThumbImage(thumb)?.alt;

    if (!card || !image || !nextSrc) return false;

    image.src = nextSrc;
    if (nextSrcset) {
      image.srcset = nextSrcset;
    } else {
      image.removeAttribute('srcset');
    }
    if (nextAlt) image.alt = nextAlt;

    setActiveThumb(thumb, card);
    return true;
  };

  const productJsonUrlForCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    const url = new URL(link.getAttribute('href'), window.location.href);
    const proxyMatch = url.pathname.match(/^\/proxy\/(mobile|desktop)(\/.*)$/);
    const proxyPrefix = proxyMatch ? `/proxy/${proxyMatch[1]}` : '';
    const productPath = proxyMatch ? proxyMatch[2] : url.pathname;
    if (!productPath.includes('/products/')) return '';

    return `${proxyPrefix}${productPath.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
  };

  const fetchProductJson = (url) => {
    if (!url) return Promise.resolve(null);
    if (!productJsonCache.has(url)) {
      productJsonCache.set(
        url,
        fetch(url, { credentials: 'same-origin', cache: 'no-store' })
          .then((response) => response.ok ? response.json() : null)
          .catch(() => null),
      );
    }
    return productJsonCache.get(url);
  };

  const collectProductImages = (productData) => {
    const images = [];
    const seen = new Set();
    const pushImage = (src, alt = '') => {
      const normalizedSrc = normalizeImageUrl(src);
      const key = imageKey(normalizedSrc);
      if (!normalizedSrc || seen.has(key)) return;
      seen.add(key);
      images.push({ src: normalizedSrc, alt });
    };

    productData?.variants?.forEach((variant) => {
      pushImage(variant.featured_image?.src, variant.public_title || variant.title || productData.title);
    });
    productData?.images?.forEach((src) => pushImage(src, productData.title));
    pushImage(productData?.featured_image, productData?.title);

    return images;
  };

  const createThumb = (productImage, active) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `nini-variant-thumbs__item${active ? ' is-active' : ''}`;
    button.setAttribute('data-nini-variant-thumb', '');
    button.setAttribute('data-nini-main-src', imageUrlWithWidth(productImage.src, 720));
    button.setAttribute(
      'data-nini-main-srcset',
      [360, 540, 720, 900].map((width) => `${imageUrlWithWidth(productImage.src, width)} ${width}w`).join(', '),
    );
    button.setAttribute('data-nini-main-alt', productImage.alt || '');
    button.setAttribute('aria-label', 'Show product image');
    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    const img = document.createElement('img');
    img.src = imageUrlWithWidth(productImage.src, 128);
    img.alt = productImage.alt || '';
    img.loading = 'lazy';
    button.appendChild(img);

    return button;
  };

  const hydrateMissingThumbs = (scope = document) => {
    getCards(scope).forEach((card) => {
      if (card.dataset.niniProductThumbsHydrated === 'true' || getThumbs(card).length > 0) return;

      const image = card.querySelector(imageSelector);
      const jsonUrl = productJsonUrlForCard(card);
      if (!image || !jsonUrl) return;

      card.dataset.niniProductThumbsHydrated = 'true';
      fetchProductJson(jsonUrl).then((productData) => {
        const productImages = collectProductImages(productData);
        if (productImages.length < 2 || !card.isConnected) return;

        const activeKey = imageKey(image.currentSrc || image.src);
        const thumbs = document.createElement('div');
        thumbs.className = 'nini-variant-thumbs';
        thumbs.setAttribute('aria-label', 'Product thumbnails');

        productImages.slice(0, 5).forEach((productImage) => {
          thumbs.appendChild(createThumb(productImage, imageKey(imageUrlWithWidth(productImage.src, 720)) === activeKey));
        });

        if (!thumbs.querySelector('.is-active')) {
          thumbs.firstElementChild?.classList.add('is-active');
          thumbs.firstElementChild?.setAttribute('aria-pressed', 'true');
        }

        const mobileShell = card.querySelector('.nini-mobile-product-card__media-shell');
        const mediaWrap = card.querySelector('.nini-product-card__media-wrap');
        if (mobileShell) {
          mobileShell.appendChild(thumbs);
        } else if (mediaWrap) {
          mediaWrap.insertAdjacentElement('afterend', thumbs);
        } else {
          card.appendChild(thumbs);
        }

        initThumbs(card);
      });
    });
  };

  function initThumbs(scope = document) {
    getCards(scope).forEach((card) => {
      const image = card.querySelector(imageSelector);
      const thumbs = getThumbs(card);
      if (!image || thumbs.length === 0) return;

      thumbs.forEach((thumb) => {
        if (thumb.tagName !== 'BUTTON') {
          thumb.setAttribute('role', 'button');
          thumb.setAttribute('tabindex', '0');
        }
        if (!thumb.hasAttribute('aria-pressed')) thumb.setAttribute('aria-pressed', 'false');
      });

      if (!thumbs.some((thumb) => thumb.classList.contains('is-active'))) {
        thumbs[0].classList.add('is-active');
        thumbs[0].setAttribute('aria-pressed', 'true');
      }
    });

    hydrateMissingThumbs(scope);
  }

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest(thumbSelector);
    if (!thumb || !thumb.closest(cardSelector)) return;

    event.preventDefault();
    event.stopPropagation();
    swapImage(thumb);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const thumb = event.target.closest(thumbSelector);
    if (!thumb || !thumb.closest(cardSelector)) return;

    event.preventDefault();
    swapImage(thumb);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initThumbs(document));
  } else {
    initThumbs(document);
  }

  document.addEventListener('shopify:section:load', (event) => initThumbs(event.target));
  document.addEventListener('nini:product-cards:refresh', (event) => initThumbs(event.detail?.scope || document));
})();
